import { relaunch } from '@tauri-apps/plugin-process';
import { check } from '@tauri-apps/plugin-updater';
import { useUpdateStore } from '../../stores/updateStore';
import { checkTauriPresentSync } from './isDesktop';

/**
 * Windows: the installer (passive mode) stops and restarts the app itself.
 * @returns {boolean}
 */
export function installerHandlesRestart() {
  return /Windows/i.test(navigator.userAgent);
}

/**
 * Check the update endpoint. Automatic (startup) checks are silent on every
 * outcome except "update available"; manual checks always report back,
 * distinguishing "up to date" from "server unreachable" (air-gapped/offline).
 * @param {{ manual?: boolean }} [options]
 * @returns {Promise<void>}
 */
export async function checkForAppUpdate({ manual = false } = {}) {
  if (!checkTauriPresentSync()) return;

  const store = useUpdateStore.getState();

  try {
    const update = await check();
    if (!update) {
      console.log('No update available');
      if (manual) store.setUpdateStatus('no-update');
      return;
    }

    console.log(`Update available: ${update.version}`);
    store.setPendingUpdate(update);
  } catch (e) {
    // Unreachable endpoint, timeout, TLS error, malformed manifest... - on
    // startup this must never surface; air-gapped installs hit it every launch.
    console.warn('Update check failed:', e);
    if (manual) store.setUpdateStatus('check-failed');
  }
}

/**
 * @param {import('@tauri-apps/plugin-updater').Update} update
 * @returns {Promise<void>}
 */
export async function downloadAndInstallUpdate(update) {
  const store = useUpdateStore.getState();
  store.setUpdateStatus('downloading');
  store.setDownloadProgress(null);

  try {
    await update.downloadAndInstall((event) => {
      switch (event.event) {
        case 'Started':
          store.setDownloadProgress({
            total: event.data.contentLength ?? null,
            downloaded: 0,
          });
          break;
        case 'Progress': {
          const current = useUpdateStore.getState().downloadProgress;
          store.setDownloadProgress({
            total: current?.total ?? null,
            downloaded: (current?.downloaded ?? 0) + (event.data.chunkLength ?? 0),
          });
          break;
        }
        case 'Finished':
          break;
      }
    });
  } catch (e) {
    // Signature mismatch, interrupted download, install failure: the
    // running install is untouched - surface it and let the user retry.
    console.error('Update install failed:', e);
    store.setErrorMessage(`Update failed: ${e}`);
    return;
  }

  store.setUpdateStatus('installed');
  if (!installerHandlesRestart()) {
    await relaunchIntoNewVersion();
  }
}

/**
 * macOS/Linux: the bundle was swapped in place; restart to run it.
 * Exit handler shuts the sidecar down.
 * @returns {Promise<void>}
 */
export async function relaunchIntoNewVersion() {
  try {
    await relaunch();
  } catch (e) {
    console.error('Relaunch failed:', e);
    useUpdateStore.getState().setUpdateStatus('restart-required');
  }
}
